// Поле даты «дд.мм.гггг» (README «Тексты»; components/Field/README.md; ux.md, разделы 5, 13)
// вместо <input type="date">: системное поле показывает дату по языку браузера, а сервис — всегда
// по-русски. Дата вводится с клавиатуры по маске (точки встают сами) или выбирается в календаре:
// кнопка справа в поле или Alt+↓. Значение — ISO «2026-10-20»; подпись, подсказка и ошибка — как
// у Field. Плейсхолдер — пример значения «например, 20.10.2026», у необязательного поля подпись —
// «— по желанию» (optional; ux.md, раздел 13). Проверку при отправке делает форма (ux.md,
// раздел 13): поле только сообщает, верен ли введённый текст.
//
// 044 (MOT-06, MOT-07): календарь уходит с выходом (t-pop-out, 120 мс), как SearchPicker: фокус —
// на кнопку календаря (дату выбрали — в поле) в начале выхода, а Esc во время выхода — следующему
// слою (окну или поповеру под полем).
import * as RadixPopover from '@radix-ui/react-popover';
import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';
import { useLayoutEffect, useRef, useState } from 'react';
import type {
  ChangeEvent,
  InputHTMLAttributes,
  KeyboardEvent as ReactKeyboardEvent,
  Ref,
  RefCallback,
} from 'react';
import { cx } from '../lib/cx.ts';
import { relayEscape } from '../lib/layers.ts';
import { altShortcut } from '../lib/platform.ts';
import {
  WEEKDAYS,
  WEEKDAYS_FULL,
  addDays,
  addMonths,
  caretAfterMask,
  clampDate,
  isInRange,
  isoToDateText,
  localToday,
  maskDateText,
  monthStart,
  monthTitle,
  monthWeeks,
  parseDateText,
  spokenDate,
  weekdayOf,
} from '../lib/date.ts';
import { useExitFocus } from '../lib/return-focus.ts';
import { Button } from './Button.tsx';
import { Field } from './Field.tsx';
import type { FieldProps } from './Field.tsx';
import { IconButton } from './IconButton.tsx';

/** Плейсхолдер поля даты — пример значения, а не маска (ux.md, раздел 13). */
export const DATE_PLACEHOLDER = 'например, 20.10.2026';

/**
 * Клавиши календаря (ux.md, раздел 8): одни и те же — в подсказках кнопок месяца и в списке `?`
 * приложения. Каждая — одно сочетание: клавиши нажимаются вместе («Shift PageUp»).
 */
export const CALENDAR_KEYS = {
  previousMonth: ['PageUp'],
  nextMonth: ['PageDown'],
  previousYear: ['Shift', 'PageUp'],
  nextYear: ['Shift', 'PageDown'],
  weekStart: ['Home'],
  weekEnd: ['End'],
} as const satisfies Readonly<Record<string, readonly string[]>>;

/** Сочетание одной надписью для подсказки и aria-keyshortcuts: «Shift+PageUp». */
function chord(keys: readonly string[]): string {
  return keys.join('+');
}

/** Что ввёл человек: текст поля и верна ли дата в нём (пустое поле — верно). */
export interface DateInputState {
  text: string;
  /** Текст не пустой и не полная существующая дата: «20.1», «31.02.2026». */
  invalid: boolean;
}

export interface DateFieldProps
  extends
    Omit<FieldProps, 'children'>,
    Omit<
      InputHTMLAttributes<HTMLInputElement>,
      | 'id'
      | 'value'
      | 'defaultValue'
      | 'onChange'
      | 'type'
      | 'min'
      | 'max'
      | 'disabled'
      | 'required'
      | 'className'
    > {
  /** Дата ISO «2026-10-20»; пусто — '' или null. */
  value: string | null | undefined;
  /**
   * Полная существующая дата — её ISO; пустое, неполное или неверное — '' (как у системного
   * поля даты). Что именно введено — во втором аргументе: форма покажет ошибку при отправке.
   */
  onChange: (value: string, input: DateInputState) => void;
  /** Первая и последняя дата для выбора в календаре (ISO); вне них дни недоступны. */
  min?: string;
  max?: string;
  /** Сегодня по поясу объекта (ISO); по умолчанию — по часам браузера. */
  today?: string;
  /** Кнопка календаря в поле; false — только ввод с клавиатуры. */
  picker?: boolean;
  ref?: Ref<HTMLInputElement>;
}

function assignRef<T>(ref: Ref<T> | undefined, value: T | null): void {
  if (typeof ref === 'function') ref(value);
  else if (ref) ref.current = value;
}

export function DateField({
  value,
  onChange,
  min,
  max,
  today,
  picker = true,
  ref,
  label,
  hint,
  error,
  required,
  optional,
  disabled,
  disabledReason,
  id,
  className,
  placeholder = DATE_PLACEHOLDER,
  onKeyDown,
  ...input
}: DateFieldProps) {
  const current = value ?? '';
  const [text, setText] = useState(() => isoToDateText(current));
  // Значение сменилось снаружи (календарь, «Сбросить», ссылка) — текст следует за ним; свой
  // неполный ввод («20.1» при пустом значении) не стирается.
  const [seen, setSeen] = useState(current);
  if (seen !== current) {
    setSeen(current);
    if ((parseDateText(text) ?? '') !== current) setText(isoToDateText(current));
  }
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const caret = useRef<number | null>(null);
  const picked = useRef(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  // Фокус — в начале выхода календаря, а не после него (044, MOT-06): на кнопку календаря, а если
  // дату выбрали — в поле, чтобы её можно было поправить с клавиатуры.
  const exitFocus = useExitFocus(open, {
    triggerRef,
    contentRef,
    onCloseAutoFocus: (event) => {
      if (!picked.current) return;
      picked.current = false;
      event.preventDefault();
      inputRef.current?.focus();
    },
  });

  useLayoutEffect(() => {
    // Маска переставила точки: каретка — после тех же цифр, что и до ввода.
    const element = inputRef.current;
    if (caret.current === null || !element || document.activeElement !== element) return;
    element.setSelectionRange(caret.current, caret.current);
    caret.current = null;
  });

  const setInputRef: RefCallback<HTMLInputElement> = (element) => {
    inputRef.current = element;
    assignRef(ref, element);
  };

  const emit = (next: string) => {
    const iso = parseDateText(next);
    onChange(iso ?? '', { text: next, invalid: next !== '' && iso === null });
  };

  const onInput = (event: ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value;
    const atEnd = (event.target.selectionStart ?? raw.length) >= raw.length;
    const before = raw.slice(0, event.target.selectionStart ?? raw.length).replace(/\D/g, '');
    const next = maskDateText(raw);
    caret.current = atEnd ? next.length : caretAfterMask(next, before.length);
    setText(next);
    emit(next);
  };

  const onInputKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>) => {
    onKeyDown?.(event);
    if (event.defaultPrevented) return;
    if (picker && event.altKey && event.key === 'ArrowDown') {
      event.preventDefault();
      setOpen(true);
    }
  };

  const pick = (iso: string) => {
    picked.current = true;
    const next = isoToDateText(iso);
    setText(next);
    emit(next);
    setOpen(false);
  };

  return (
    <Field {...{ label, hint, error, required, optional, disabled, disabledReason, id, className }}>
      {(control) => (
        <div className={cx('kp-date', picker && 'has-picker')}>
          <input
            {...input}
            {...control}
            ref={setInputRef}
            type="text"
            inputMode="numeric"
            autoComplete="off"
            maxLength={10}
            placeholder={placeholder}
            value={text}
            onChange={onInput}
            onKeyDown={onInputKeyDown}
            className="t-input"
          />
          {picker ? (
            <RadixPopover.Root open={open} onOpenChange={setOpen} modal>
              <RadixPopover.Trigger asChild ref={triggerRef}>
                <IconButton
                  icon={CalendarDays}
                  label="Выбрать дату"
                  // Одно сочетание, а не два шага: «Alt+↓» (на Mac — «⌥↓»).
                  shortcut={altShortcut('↓')}
                  size="sm"
                  disabled={control.disabled}
                  className="kp-date-btn"
                  // Имя календаря — с подписью поля: «Выбрать дату: С».
                  aria-describedby={`${control.id}-label`}
                />
              </RadixPopover.Trigger>
              <RadixPopover.Portal>
                <RadixPopover.Content
                  ref={contentRef}
                  align="end"
                  sideOffset={4}
                  collisionPadding={8}
                  className="t-pop kp-pop kp-popover kp-cal-pop"
                  aria-label={typeof label === 'string' ? `Выбор даты: ${label}` : 'Выбор даты'}
                  onOpenAutoFocus={(event) => {
                    // Фокус — на выбранном дне, иначе на сегодняшнем (components/Dropdown/README.md).
                    event.preventDefault();
                    const content = event.currentTarget;
                    if (content instanceof HTMLElement) {
                      content.querySelector<HTMLElement>('.kp-cal-day[tabindex="0"]')?.focus();
                    }
                  }}
                  // Панель модальная: как у Radix, фокус на кнопку и после нажатия снаружи.
                  onCloseAutoFocus={exitFocus.onCloseAutoFocus}
                  // Esc, пока календарь уходит, — следующему слою (044, MOT-07).
                  onEscapeKeyDown={(event) => {
                    if (open) return;
                    event.preventDefault();
                    relayEscape(contentRef.current, event);
                  }}
                >
                  <Calendar
                    value={parseDateText(text)}
                    today={today ?? localToday()}
                    min={min}
                    max={max}
                    onPick={pick}
                    onClear={text !== '' ? () => pick('') : undefined}
                  />
                </RadixPopover.Content>
              </RadixPopover.Portal>
            </RadixPopover.Root>
          ) : null}
        </div>
      )}
    </Field>
  );
}

/** Свойства календаря (026: календарь у ячейки даты в таблице протокола — тот же компонент). */
export interface CalendarProps {
  /** Выбранный день (ISO «2026-10-20»); null — нет выбранного, фокус на сегодняшнем. */
  value: string | null;
  /** Сегодня (ISO): обведено, кнопки «Сегодня» и «Завтра». */
  today: string;
  /** Первая и последняя дата для выбора (ISO); вне них дни недоступны. */
  min?: string;
  max?: string;
  /** Выбран день: щелчок, Enter на дне, «Сегодня», «Завтра». */
  onPick: (iso: string) => void;
  /** «Очистить»; нет — кнопки нет. */
  onClear?: () => void;
}

/**
 * Мини-календарь (ux.md, раздел 5): сверху быстрые пилюли «Сегодня», «Завтра» и красная
 * «Очистить», ниже месяц. Сегодня обведено color-accent, выбранное залито color-accent.
 * Клавиши: стрелки — день и неделя, Home и End — начало и конец недели, PageUp и PageDown —
 * месяц (с Shift — год), Enter — выбрать, Esc — закрыть. PageUp и PageDown работают во всём
 * календаре, не только на дне: клавиша — в подсказках «Предыдущий месяц» и «Следующий месяц»
 * (ux.md, раздел 8), а все клавиши календаря — в списке `?` (CALENDAR_KEYS).
 *
 * 026: календарь — и у ячейки даты таблицы протокола, без поля (CellCalendar); Esc закрывает
 * панель вокруг него. Фокус при открытии ставит панель: на день с tabindex="0" (выбранный, иначе
 * сегодняшний).
 */
export function Calendar({ value, today, min, max, onPick, onClear }: CalendarProps) {
  const [focused, setFocused] = useState(() => clampDate(value ?? today, min, max));
  const [month, setMonth] = useState(() => monthStart(focused));
  const gridRef = useRef<HTMLTableElement>(null);
  const moved = useRef(false);

  useLayoutEffect(() => {
    // Стрелки перевели день — фокус за ним, в том числе в соседний месяц.
    if (!moved.current) return;
    moved.current = false;
    gridRef.current?.querySelector<HTMLElement>('.kp-cal-day[tabindex="0"]')?.focus();
  });

  const moveTo = (iso: string) => {
    moved.current = true;
    setFocused(iso);
    setMonth(monthStart(iso));
  };

  const showMonth = (step: number) => {
    const next = addMonths(month, step);
    setMonth(next);
    setFocused(clampDate(addMonths(focused, step), min, max));
  };

  // PageUp и PageDown вне дней (кнопки месяца, «Сегодня»): тот же шаг, что и с дня; фокус — на месте.
  const onCalendarKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.defaultPrevented) return;
    if (event.key !== 'PageUp' && event.key !== 'PageDown') return;
    event.preventDefault();
    showMonth((event.key === 'PageUp' ? -1 : 1) * (event.shiftKey ? 12 : 1));
  };

  const onDayKeyDown = (event: ReactKeyboardEvent<HTMLButtonElement>, iso: string) => {
    const steps: Record<string, () => string> = {
      ArrowLeft: () => addDays(iso, -1),
      ArrowRight: () => addDays(iso, 1),
      ArrowUp: () => addDays(iso, -7),
      ArrowDown: () => addDays(iso, 7),
      Home: () => addDays(iso, -weekdayOf(iso)),
      End: () => addDays(iso, 6 - weekdayOf(iso)),
      PageUp: () => addMonths(iso, event.shiftKey ? -12 : -1),
      PageDown: () => addMonths(iso, event.shiftKey ? 12 : 1),
    };
    const step = steps[event.key];
    if (!step) return;
    event.preventDefault();
    moveTo(step());
  };

  const title = monthTitle(month);
  const tomorrow = addDays(today, 1);
  // В показанном месяце всегда есть день, на который ведёт Tab: выбранный фокусом или первый.
  const tabbable = monthStart(focused) === month ? focused : month;
  return (
    <div className="kp-cal" onKeyDown={onCalendarKeyDown}>
      <div className="kp-cal-quick">
        <Button size="sm" disabled={!isInRange(today, min, max)} onClick={() => onPick(today)}>
          Сегодня
        </Button>
        <Button
          size="sm"
          disabled={!isInRange(tomorrow, min, max)}
          onClick={() => onPick(tomorrow)}
        >
          Завтра
        </Button>
        <span className="kp-grow" />
        {onClear ? (
          <Button variant="ghost" size="sm" className="kp-cal-clear" onClick={onClear}>
            Очистить
          </Button>
        ) : null}
      </div>
      <div className="kp-cal-h">
        <IconButton
          icon={ChevronLeft}
          label="Предыдущий месяц"
          shortcut={chord(CALENDAR_KEYS.previousMonth)}
          aria-keyshortcuts={chord(CALENDAR_KEYS.previousMonth)}
          size="sm"
          onClick={() => showMonth(-1)}
        />
        <span className="kp-cal-title" aria-live="polite">
          {title}
        </span>
        <IconButton
          icon={ChevronRight}
          label="Следующий месяц"
          shortcut={chord(CALENDAR_KEYS.nextMonth)}
          aria-keyshortcuts={chord(CALENDAR_KEYS.nextMonth)}
          size="sm"
          onClick={() => showMonth(1)}
        />
      </div>
      <table ref={gridRef} role="grid" aria-label={title} className="kp-cal-grid">
        <thead>
          <tr>
            {WEEKDAYS.map((day, index) => (
              <th key={day} scope="col" abbr={WEEKDAYS_FULL[index]}>
                {day}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {monthWeeks(month).map((week, index) => (
            <tr key={index}>
              {week.map((iso, day) =>
                iso === null ? (
                  <td key={`empty-${day}`} />
                ) : (
                  <td key={iso} aria-selected={iso === value ? true : undefined}>
                    <button
                      type="button"
                      tabIndex={iso === tabbable ? 0 : -1}
                      className={cx(
                        'kp-cal-day',
                        iso === today && 'is-today',
                        iso === value && 'is-selected',
                      )}
                      aria-label={iso === today ? `${spokenDate(iso)}, сегодня` : spokenDate(iso)}
                      aria-current={iso === today ? 'date' : undefined}
                      aria-disabled={isInRange(iso, min, max) ? undefined : true}
                      onClick={() => {
                        if (isInRange(iso, min, max)) onPick(iso);
                      }}
                      onFocus={() => setFocused(iso)}
                      onKeyDown={(event) => onDayKeyDown(event, iso)}
                    >
                      {Number(iso.slice(8))}
                    </button>
                  </td>
                ),
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
